import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { DroneFlightCreateDto, DroneFlightDto, DroneFlightExcelDownloadDto, DroneFlightUpsertResultDto, DroneFlightUpdateDto, GetDroneFlightsInputDto } from '../dtos/drone-flight/models';
import type { DownloadTokenResultDto, GetByIdsInputDto } from '../dtos/shared/models';

@Injectable({
  providedIn: 'root',
})
export class DroneFlightService {
  private restService = inject(RestService);
  apiName = 'Inventory';
  

  create = (input: DroneFlightCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DroneFlightDto>({
      method: 'POST',
      url: '/api/inventory/droneFlights',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/inventory/droneFlights/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteByIds = (ids: number[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/inventory/droneFlights',
      params: { ids },
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DroneFlightDto>({
      method: 'GET',
      url: `/api/inventory/droneFlights/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getDownloadToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DownloadTokenResultDto>({
      method: 'GET',
      url: '/api/inventory/droneFlights/download-token',
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetDroneFlightsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<DroneFlightDto>>({
      method: 'GET',
      url: '/api/inventory/droneFlights',
      params: { silvicultureVendorId: input.silvicultureVendorId, flightVendorId: input.flightVendorId, countingVendorId: input.countingVendorId, rotationId: input.rotationId, area: input.area, areaFrom: input.areaFrom, areaTo: input.areaTo, flightDate: input.flightDate, flightDateFrom: input.flightDateFrom, flightDateTo: input.flightDateTo, spacingId: input.spacingId, periodId: input.periodId, treeCount: input.treeCount, treeCountFrom: input.treeCountFrom, treeCountTo: input.treeCountTo, realDensity: input.realDensity, realDensityFrom: input.realDensityFrom, realDensityTo: input.realDensityTo, theoreticalDensity: input.theoreticalDensity, theoreticalDensityFrom: input.theoreticalDensityFrom, theoreticalDensityTo: input.theoreticalDensityTo, geneticMaterialId: input.geneticMaterialId, survivalPercentage: input.survivalPercentage, survivalPercentageFrom: input.survivalPercentageFrom, survivalPercentageTo: input.survivalPercentageTo, isLastInventory: input.isLastInventory, inventoryTypeId: input.inventoryTypeId, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListAsExcelFile = (input: DroneFlightExcelDownloadDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/inventory/droneFlights/as-excel-file',
      params: { downloadToken: input.downloadToken, fields: input.fields, silvicultureVendorId: input.silvicultureVendorId, flightVendorId: input.flightVendorId, countingVendorId: input.countingVendorId, rotationId: input.rotationId, area: input.area, areaFrom: input.areaFrom, areaTo: input.areaTo, flightDate: input.flightDate, flightDateFrom: input.flightDateFrom, flightDateTo: input.flightDateTo, spacingId: input.spacingId, periodId: input.periodId, treeCount: input.treeCount, treeCountFrom: input.treeCountFrom, treeCountTo: input.treeCountTo, realDensity: input.realDensity, realDensityFrom: input.realDensityFrom, realDensityTo: input.realDensityTo, theoreticalDensity: input.theoreticalDensity, theoreticalDensityFrom: input.theoreticalDensityFrom, theoreticalDensityTo: input.theoreticalDensityTo, geneticMaterialId: input.geneticMaterialId, survivalPercentage: input.survivalPercentage, survivalPercentageFrom: input.survivalPercentageFrom, survivalPercentageTo: input.survivalPercentageTo, isLastInventory: input.isLastInventory, inventoryTypeId: input.inventoryTypeId, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListByIds = (input: GetByIdsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DroneFlightDto[]>({
      method: 'GET',
      url: '/api/inventory/droneFlights/by-ids',
      params: { ids: input.ids },
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: DroneFlightUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DroneFlightDto>({
      method: 'PUT',
      url: `/api/inventory/droneFlights/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  upsert = (input: DroneFlightCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, DroneFlightUpsertResultDto>({
      method: 'POST',
      url: '/api/inventory/droneFlights/upsert',
      body: input,
    },
    { apiName: this.apiName,...config });

}